import React, { useState } from 'react'

/**
 * ImageUploader Component
 * 
 * Reusable component for uploading multiple images with:
 * - Preview before upload
 * - Primary image selection
 * - Optional captions
 * - Image compression
 * 
 * Props:
 * - maxImages: Maximum number of images (default: 5)
 * - onImagesChange: Callback when images change
 * - existingImages: Array of existing image objects to display
 */
function ImageUploader({ 
  maxImages = 5, 
  onImagesChange,
  existingImages = []
}) {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [primaryIndex, setPrimaryIndex] = useState(0)
  const [captions, setCaptions] = useState({})
  const [error, setError] = useState('')

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    
    // Check total count
    if (selectedFiles.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`)
      return
    }

    // Validate each file
    const validFiles = []
    for (const file of files) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError(`${file.name} is not an image file`)
        continue
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name} is larger than 5MB`)
        continue
      }

      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    // Create previews
    const newPreviews = []
    validFiles.forEach((file, index) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        newPreviews.push({
          file: file,
          preview: reader.result,
          isPrimary: selectedFiles.length === 0 && index === 0, // First image is primary
          caption: ''
        })

        if (newPreviews.length === validFiles.length) {
          setSelectedFiles(prev => [...prev, ...validFiles])
          setPreviews(prev => [...prev, ...newPreviews])
          setError('')
          
          // Notify parent
          if (onImagesChange) {
            onImagesChange([...selectedFiles, ...validFiles], {
              primaryIndex: selectedFiles.length === 0 ? 0 : primaryIndex,
              captions
            })
          }
        }
      }
      reader.readAsDataURL(file)
    })
  }

  // Remove image
  const handleRemoveImage = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    const newPreviews = previews.filter((_, i) => i !== index)
    
    setSelectedFiles(newFiles)
    setPreviews(newPreviews)

    // Adjust primary index if needed
    let newPrimaryIndex = primaryIndex
    if (index === primaryIndex && newFiles.length > 0) {
      newPrimaryIndex = 0
      setPrimaryIndex(0)
    } else if (index < primaryIndex) {
      newPrimaryIndex = primaryIndex - 1
      setPrimaryIndex(primaryIndex - 1)
    }

    if (onImagesChange) {
      onImagesChange(newFiles, {
        primaryIndex: newPrimaryIndex,
        captions
      })
    }
  }

  // Set primary image
  const handleSetPrimary = (index) => {
    setPrimaryIndex(index)
    if (onImagesChange) {
      onImagesChange(selectedFiles, {
        primaryIndex: index,
        captions
      })
    }
  }

  // Update caption
  const handleCaptionChange = (index, caption) => {
    const newCaptions = { ...captions, [index]: caption }
    setCaptions(newCaptions)
    if (onImagesChange) {
      onImagesChange(selectedFiles, {
        primaryIndex,
        captions: newCaptions
      })
    }
  }

  return (
    <div className="image-uploader">
      <div className="upload-header">
        <h3>Add Images (Optional)</h3>
        <p className="upload-hint">
          Upload up to {maxImages} images. First image will be the primary image.
        </p>
      </div>

      {error && (
        <div className="alert-error">{error}</div>
      )}

      {/* File Input */}
      {selectedFiles.length < maxImages && (
        <div className="upload-input-container">
          <label htmlFor="image-upload" className="upload-button">
            📷 Select Images
          </label>
          <input
            type="file"
            id="image-upload"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <small className="upload-note">
            Accepted: JPG, PNG, WebP | Max 5MB per image
          </small>
        </div>
      )}

      {/* Existing Images Display */}
      {existingImages.length > 0 && (
        <div className="existing-images-section">
          <h4>Current Images</h4>
          <div className="images-grid">
            {existingImages.map((img, index) => (
              <div key={`existing-${index}`} className="image-preview-card existing">
                <img src={img.image_url} alt={img.caption || 'Existing'} />
                {img.is_primary && (
                  <div className="primary-badge">⭐ Primary</div>
                )}
                {img.caption && (
                  <p className="image-caption">{img.caption}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Grid */}
      {previews.length > 0 && (
        <div className="images-preview-grid">
          {previews.map((preview, index) => (
            <div key={index} className="image-preview-card">
              <img src={preview.preview} alt={`Preview ${index + 1}`} />
              
              {/* Primary Badge */}
              {index === primaryIndex && (
                <div className="primary-badge">⭐ Primary</div>
              )}

              {/* Actions */}
              <div className="image-actions">
                {index !== primaryIndex && (
                  <button
                    type="button"
                    onClick={() => handleSetPrimary(index)}
                    className="btn-set-primary"
                    title="Set as primary image"
                  >
                    ⭐ Set Primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="btn-remove-image"
                  title="Remove image"
                >
                  🗑️ Remove
                </button>
              </div>

              {/* Caption Input */}
              <input
                type="text"
                placeholder="Add caption (optional)"
                value={captions[index] || ''}
                onChange={(e) => handleCaptionChange(index, e.target.value)}
                className="caption-input"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUploader